import React, { useState, useEffect, useMemo } from "react";

import { parse, ParseResult } from "papaparse";

import { Modal, Upload, Alert, Button } from "antd";

import { useTranslation } from "@opendash/core";
import { RcFile } from "antd/lib/upload";

export default function GreisTranspondersImport({
  open,
  close,
  transponders,
  addTransponders,
  overwriteTransponders,
}) {
  const t = useTranslation();
  const [file, setFile] = useState<RcFile>();
  const [csv, setCsv] = useState<ParseResult<any>>();

  useEffect(() => {
    if (file) {
      parse(file, {
        header: true,
        skipEmptyLines: "greedy",
        complete: (result) => {
          setCsv(result);
        },
      });
    } else {
      setCsv(undefined);
    }
  }, [file]);

  const rows = useMemo(
    () =>
      csv && csv.errors.length === 0
        ? csv.data.map((row) => ({
            id: row[csv.meta.fields[0]],
            misc: row[csv.meta.fields[2]],
            misc2: row[csv.meta.fields[3]],
            owner: row[csv.meta.fields[1]],
          }))
        : [],
    [csv]
  );

  // Doppelte Transpondernummern werden nicht importiert: die ID ist die
  // Zeilen-Identität, doppelte IDs machen Bearbeiten und Speichern unbrauchbar.
  const dedupe = (existingIds: Set<string>) => {
    const seen = new Set(existingIds);
    const accepted = [];
    let skipped = 0;

    for (const row of rows) {
      if (seen.has(row.id)) {
        skipped++;
        continue;
      }

      seen.add(row.id);
      accepted.push(row);
    }

    return { accepted, skipped };
  };

  const existingIds = useMemo(
    () =>
      new Set<string>(
        (transponders ?? [])
          .filter((row) => !row._deleted)
          .map((row) => row.id)
      ),
    [transponders]
  );

  const forAdd = useMemo(() => dedupe(existingIds), [rows, existingIds]);
  const forOverwrite = useMemo(() => dedupe(new Set<string>()), [rows]);

  const clearAndClose = () => {
    setFile(undefined);
    close();
  };

  return (
    <Modal
      title={t("greis:transponders.admin.import")}
      visible={open}
      onOk={(e) => clearAndClose()}
      onCancel={(e) => clearAndClose()}
      footer={[
        <Button key="back" onClick={(e) => clearAndClose()}>
          {t("greis:transponders.admin.import_action_cancel")}
        </Button>,
        <Button
          key="import_action_add"
          type="primary"
          disabled={forAdd.accepted.length === 0}
          onClick={(e) => {
            addTransponders(forAdd.accepted);

            clearAndClose();
          }}
          children={t("greis:transponders.admin.import_action_add")}
        />,
        <Button
          key="import_action_overwrite"
          type="primary"
          disabled={forOverwrite.accepted.length === 0}
          onClick={(e) => {
            overwriteTransponders(forOverwrite.accepted);

            clearAndClose();
          }}
          children={t("greis:transponders.admin.import_action_overwrite")}
        />,
      ]}
    >
      <Preview
        preview={csv}
        skippedInFile={forOverwrite.skipped}
        skippedExisting={forAdd.skipped - forOverwrite.skipped}
      />

      <Upload.Dragger
        multiple={false}
        fileList={file ? [file] : []}
        beforeUpload={(file) => {
          setFile(file);
          return false;
        }}
        onRemove={(file) => {
          setFile(undefined);
        }}
      >
        <p className="ant-upload-text">
          {t("greis:transponders.admin.import_upload_text")}
        </p>
        <p className="ant-upload-hint">
          {t("greis:transponders.admin.import_upload_hint")}
        </p>
      </Upload.Dragger>
    </Modal>
  );
}

function Preview({ preview, skippedInFile, skippedExisting }) {
  const t = useTranslation();

  const style = { marginBottom: 24 };

  if (!preview) {
    return null;
  }

  if (preview.errors.length > 0) {
    return (
      <Alert
        type="error"
        style={style}
        message={t("greis:transponders.admin.import_bad_csv")}
      />
    );
  }

  return (
    <>
      <Alert
        type="info"
        style={style}
        message={t("greis:transponders.admin.import_success", {
          count: preview.data.length,
        })}
      />

      {skippedInFile > 0 && (
        <Alert
          type="warning"
          style={style}
          message={t("greis:transponders.admin.import_skipped_in_file", {
            count: skippedInFile,
          })}
        />
      )}

      {skippedExisting > 0 && (
        <Alert
          type="warning"
          style={style}
          message={t("greis:transponders.admin.import_skipped", {
            count: skippedExisting,
          })}
        />
      )}
    </>
  );
}
